import React from 'react'
import {Segment} from './types'
import {SegmentMediaRef, SegmentTimeMap} from './types'
import {getActualStartingTimes} from './utils'
const MINIMUM_SECONDS_BUFFER = 2
interface SegmentRefsControl {
  setVolume: (volume: number) => void
  mediaRefs: SegmentMediaRef[]
  ready: boolean
  activePart: number
  segmentedTimeMapping: SegmentTimeMap[]
  checkIfReady: () => void
  onProgress: (index: number) => (event: any) => void
  onSeeked: (index: number) => (event: any) => void
  setPartCurrentTime: (index: number, currentTime: number) => void
  updatePartPlayState: (state: 'play' | 'pause', index: number) => void
  isBuffering: boolean
}
type videoProcessPhase = 'init' | 'loaded' | 'processed'
const READY_STATE = {
  HAVE_FUTURE_DATA: 3,
  HAVE_ENOUGH_DATA: 4
}

const isMediaElementStateValid = (state?: number): boolean =>
  state === READY_STATE.HAVE_ENOUGH_DATA ||
  state === READY_STATE.HAVE_FUTURE_DATA

interface HookInput {
  segments: Segment[]
  isLoading: boolean
}

export const useSegmentRefsControls = ({segments, isLoading}: HookInput): SegmentRefsControl => {
  const [processPhase, setProcessPhase] = React.useState<videoProcessPhase>('init')
  const [segmentedTimeMapping, setSegmentedTimeMapping] = React.useState<SegmentTimeMap[]>([])
  const [activePart, setActivePart] = React.useState(0)
  const [firstVideoBuffered, setFirstVideoBuffered] = React.useState<boolean>(false)
  const [isBuffering, setIsBuffering] = React.useState<boolean>(true)
  const [mediaRefs, setMediaRefs] = React.useState<SegmentMediaRef[]>(
    segments.map(() => ({
      video: React.createRef<HTMLVideoElement>(),
      audio: React.createRef<HTMLAudioElement>()
    }))
  )

  React.useEffect(
    () => {
      setFirstVideoBuffered(false)
      setProcessPhase('init')
      setMediaRefs(
        segments.map(() => ({
          video: React.createRef<HTMLVideoElement>(),
          audio: React.createRef<HTMLAudioElement>()
        }))
      )
    },
    [segments, isLoading]
  )

  const checkIfReady = React.useCallback(() => {
    if (
      processPhase === 'init' &&
      !isLoading &&
      firstVideoBuffered &&
      mediaRefs.every(
        ({video, audio}) =>
          isMediaElementStateValid(video.current?.readyState) &&
          (audio.current ? isMediaElementStateValid(audio.current.readyState) : true)
      )
    ) {
      setProcessPhase('loaded')
      getActualStartingTimes(mediaRefs).then(timeMaps => {
        setSegmentedTimeMapping(timeMaps)
        setProcessPhase('processed')
      })
    }
  }, [mediaRefs, isLoading, processPhase, firstVideoBuffered])

  const onProgress = React.useCallback((index) => ({target}: any) => {
    const {buffered, duration, readyState, networkState} = target
    setIsBuffering(readyState < target.HAVE_CURRENT_DATA)

    if (firstVideoBuffered) {return}
    // buffer to time\percent
    const secondsBuffered = buffered.length === 0 ? 0 : buffered.end(0)
    const progress = duration > 0
      ? (secondsBuffered / duration)
      : 0

    if ((progress === 1 || secondsBuffered >= MINIMUM_SECONDS_BUFFER || readyState === READY_STATE.HAVE_ENOUGH_DATA)
      && index === 0) {
      setFirstVideoBuffered(true)
    }
  }, [firstVideoBuffered])

  const onSeeked = React.useCallback(() => () => {
    setIsBuffering(false)
  }, [firstVideoBuffered])

  React.useEffect(() => {
    if (mediaRefs.length > 0 && firstVideoBuffered) {
      checkIfReady()
    }
  }, [checkIfReady, firstVideoBuffered])

  const mediaRefsCallbacks = React.useMemo(
    () => ({
      setPartCurrentTime: (index: number, currentTime: number) => {
        setActivePart(index)
        ; (mediaRefs[index].video.current as HTMLVideoElement).currentTime = currentTime
        if (mediaRefs[index].audio.current) {
          (mediaRefs[index].audio.current as HTMLVideoElement).currentTime = currentTime
        }
      },
      updatePartPlayState: (state: 'play' | 'pause', index: number) => {
        if (state === 'play') {
          mediaRefs[index].video.current?.play()
          mediaRefs[index].audio.current?.play()
        } else {
          mediaRefs[index].video.current?.pause()
          mediaRefs[index].audio.current?.pause()
        }
      },
      setVolume: (newVolume: number) =>
        mediaRefs.forEach(({audio}) => {
          if (audio.current) {
            (audio.current as HTMLMediaElement).volume = newVolume
          }
        })
    }),
    [mediaRefs]
  )

  return {
    mediaRefs,
    ready: processPhase === 'processed',
    segmentedTimeMapping,
    activePart,
    checkIfReady,
    onProgress,
    isBuffering,
    onSeeked,
    ...mediaRefsCallbacks
  }
}
